import {
  ArrowLeft, CalendarClock, CalendarDays, Clock, FileText, History, Mail, MapPin, MessageSquareText, Pencil, Phone,
  Share2, Trash2, UserRound, UserRoundCheck, UserRoundPlus, UsersRound, type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch, Tooltip } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { AppointmentStatusLabel } from "@/features/appointment-status";
import { ClassAttendees } from "@/features/class-attendees";
import { DayAndTime } from "@/features/day-and-time";
import { ApiError } from "@/lib/api";
import {
  SETTABLE_STATUSES, SOURCE_LABELS, confirmationText, eventText, isClosed, locationText, remindersChangeable,
  statusInfo, timeRange,
} from "@/lib/appointments";
import { chosenTime, openWhen, withSlot, type When } from "@/lib/booking-time";
import { LOCATION_KINDS, LOCATION_LABELS, LOCATION_PLACEHOLDERS, localDay } from "@/lib/calendars";
import {
  useAppointment, useCalendar, useRescheduleAppointment, useSetAppointmentStatus, useUpdateAppointment,
} from "@/lib/queries";
import type { AppointmentDetail, MeetingLocationKind } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

export type DrawerMode = "view" | "edit" | "reschedule";
type Mode = DrawerMode;

/**
 * One appointment's details (ACL-291), as GoHighLevel's "View details" drawer shows them: when, the
 * contact, the owner, where, the calendar, the attendees, who booked it and how, its description,
 * and the status at the foot. Pencil edits the words and the place; the clock reschedules; the bin
 * cancels — an appointment is never deleted, so its history stays. A cancelled or rescheduled
 * appointment is closed: it reads, and a rescheduled one links to its new time. What the contact is
 * texted (ACL-504) is under their details: the confirmation sent with the booking, and the reminders,
 * turned on or off here until it starts.
 */
export function AppointmentDrawer({
  appointmentId, onOpenChange, onOpenAppointment, startIn = "view",
}: {
  appointmentId: number | null;
  onOpenChange: (open: boolean) => void;
  /** Open another appointment in the drawer: the new time after a reschedule. */
  onOpenAppointment: (id: number) => void;
  /** Open on the details, or straight on rescheduling (the list's row menu). */
  startIn?: Mode;
}) {
  const { data, isPending, error } = useAppointment(appointmentId ?? undefined);
  const [mode, setMode] = React.useState<Mode>(startIn);
  React.useEffect(() => setMode(startIn), [appointmentId, startIn]);

  return (
    <Dialog open={appointmentId !== null} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        {isPending ? (
          <DialogBody className="grid place-items-center py-24">
            <DialogTitle className="sr-only">Appointment</DialogTitle>
            <Spinner />
          </DialogBody>
        ) : error || !data ? (
          <DialogBody>
            <DialogTitle className="sr-only">Appointment</DialogTitle>
            <ErrorState error={error} title="Could not load the appointment" />
          </DialogBody>
        ) : mode === "edit" ? (
          <EditForm appointment={data} onDone={() => setMode("view")} />
        ) : mode === "reschedule" ? (
          <Reschedule
            appointment={data}
            onDone={(newId) => {
              setMode("view");
              if (newId) onOpenAppointment(newId);
            }}
          />
        ) : (
          <Details appointment={data} onMode={setMode} onOpenAppointment={onOpenAppointment} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-text-muted" aria-hidden />
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        <div className="break-words text-sm text-text">{children}</div>
      </div>
    </div>
  );
}

function Details({
  appointment: a, onMode, onOpenAppointment,
}: {
  appointment: AppointmentDetail;
  onMode: (mode: Mode) => void;
  onOpenAppointment: (id: number) => void;
}) {
  const toast = useToast();
  const setStatus = useSetAppointmentStatus();
  const calendar = useCalendar(a.calendar_id);
  const format = calendar.data?.time_format ?? "12h";
  const locale = calendar.data?.locale ?? "en-US";
  const closed = isClosed(a.status);
  const [cancelling, setCancelling] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const status = statusInfo(a.status);

  const change = (next: string, note?: string) =>
    setStatus.mutate(
      { id: a.id, status: next, note: note?.trim() || null },
      {
        onSuccess: () => {
          setCancelling(false);
          toast.success(next === "cancelled" ? "Appointment cancelled" : `Marked ${statusInfo(next).label.toLowerCase()}`, a.title);
        },
        onError: (e) => toast.error("Could not change the status", e instanceof ApiError ? e.message : "Please try again."),
      },
    );

  return (
    <>
      <DialogHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <DialogTitle>View details</DialogTitle>
        {!closed && (
          <span className="flex items-center gap-0.5 pr-8">
            <Tooltip content="Edit">
              <Button variant="ghost" size="icon-sm" aria-label="Edit appointment" onClick={() => onMode("edit")}>
                <Pencil />
              </Button>
            </Tooltip>
            <Tooltip content="Reschedule">
              <Button variant="ghost" size="icon-sm" aria-label="Reschedule appointment" onClick={() => onMode("reschedule")}>
                <CalendarClock />
              </Button>
            </Tooltip>
            <Tooltip content="Cancel appointment">
              <Button variant="ghost" size="icon-sm" aria-label="Cancel appointment" onClick={() => setCancelling(true)}>
                <Trash2 />
              </Button>
            </Tooltip>
          </span>
        )}
      </DialogHeader>
      <DialogBody className="space-y-5">
        <div className="space-y-2 border-b border-border pb-4">
          <span className="flex items-center gap-2">
            <Badge tone="accent" size="sm">Appointment</Badge>
            <Badge tone={status.tone} size="sm">{status.label}</Badge>
          </span>
          <h3 className="text-lg font-semibold text-text">{a.title}</h3>
        </div>

        {a.status === "rescheduled" && (
          <Callout tone="info">
            This appointment was rescheduled.{" "}
            {a.rescheduled_to_id && (
              <button type="button" className="font-medium underline" onClick={() => onOpenAppointment(a.rescheduled_to_id!)}>
                Open its new time
              </button>
            )}
          </Callout>
        )}
        {a.rescheduled_from_id && (
          <p className="text-xs text-text-muted">
            Rescheduled from{" "}
            <button type="button" className="underline" onClick={() => onOpenAppointment(a.rescheduled_from_id!)}>
              an earlier time
            </button>
            .
          </p>
        )}

        <div className="space-y-4">
          <Row icon={Clock} label="Appointment time">
            {timeRange(a.starts_at, a.ends_at, a.calendar_timezone, format, locale)}
          </Row>
          {a.calendar_type === "class" ? (
            <Row icon={UsersRound} label="Class">
              <ClassAttendees appointment={a} />
            </Row>
          ) : (
            <Row icon={UserRound} label="Contact name">
              {a.contact ? (
                <Link to={`/contacts/${a.contact.id}`} className="font-medium text-accent-11 hover:underline">
                  {a.contact.name ?? a.contact.phone}
                </Link>
              ) : (
                <span className="text-text-muted">No contact</span>
              )}
            </Row>
          )}
          {a.contact && (
            <Row icon={Phone} label="Phone">
              {a.contact.phone}
            </Row>
          )}
          {a.contact?.email && (
            <Row icon={Mail} label="Email">
              {a.contact.email}
            </Row>
          )}
          {a.contact && a.calendar_type !== "class" && (
            <Row icon={MessageSquareText} label="Texts to the contact">
              <ContactTexts appointment={a} />
            </Row>
          )}
          <Row icon={UserRoundCheck} label="Appointment owner">
            {a.owner?.name ?? "—"}
          </Row>
          <Row icon={MapPin} label={`Location · ${LOCATION_LABELS[a.location_kind]}`}>
            {locationText(a.location_kind, a.location_detail, a.contact?.phone)}
          </Row>
          <Row icon={CalendarDays} label="Calendar">
            {a.calendar_name}
          </Row>
          {a.calendar_type === "collaborative" ? (
            <Row icon={UsersRound} label="Hosts">
              {a.hosts.map((h) => h.name).join(", ") || "—"}
            </Row>
          ) : a.calendar_type !== "class" ? (
            <Row icon={UsersRound} label="Attendees">
              {[a.contact?.name ?? a.contact?.phone, ...a.hosts.map((h) => h.name)].filter(Boolean).join(", ") || "—"}
            </Row>
          ) : null}
          <Row icon={UserRoundPlus} label="Booked by">
            {a.booked_by?.name ?? "—"}
          </Row>
          <Row icon={Share2} label="Source">
            {SOURCE_LABELS[a.source] ?? a.source}
          </Row>
          <Row icon={FileText} label="Appointment description">
            {a.notes ? <span className="whitespace-pre-wrap">{a.notes}</span> : <span className="text-text-muted">—</span>}
          </Row>
        </div>

        <div className="space-y-2 border-t border-border pt-4">
          <p className="flex items-center gap-1.5 text-xs font-medium text-text">
            <History className="size-3.5" aria-hidden />
            History
          </p>
          <ol className="space-y-1.5">
            {a.events.map((e, i) => (
              <li key={i} className="text-xs">
                <span className="text-text">{eventText(e)}</span>
                <span className="text-text-muted">
                  {" · "}
                  {e.by ?? "System"} · {formatDateTime(e.created_at)}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </DialogBody>
      <DialogFooter className="flex-row items-center justify-start sm:justify-start">
        <span className="text-sm text-text-muted">Status:</span>
        <Select value={a.status} disabled={closed || setStatus.isPending} onValueChange={(next) => (next === "cancelled" ? setCancelling(true) : change(next))}>
          <SelectTrigger className="w-44" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(closed ? [a.status] : SETTABLE_STATUSES).map((s) => (
              <SelectItem key={s} value={s}>
                <AppointmentStatusLabel status={s} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DialogFooter>

      <ConfirmDialog
        open={cancelling}
        onOpenChange={(open) => {
          setCancelling(open);
          if (!open) setReason("");
        }}
        title="Cancel this appointment?"
        description={
          <span className="block space-y-2">
            <span className="block">
              Its time is freed for other bookings. The appointment stays in the history as cancelled and cannot be
              reopened.
            </span>
            <Textarea
              aria-label="Reason (optional)"
              placeholder="Reason (optional)"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </span>
        }
        confirmLabel="Cancel appointment"
        cancelLabel="Keep it"
        destructive
        loading={setStatus.isPending}
        onConfirm={() => change("cancelled", reason)}
      />
    </>
  );
}

/** The confirmation the booking texted the contact, and whether they are texted reminders (ACL-504). */
function ContactTexts({ appointment: a }: { appointment: AppointmentDetail }) {
  const toast = useToast();
  const update = useUpdateAppointment();
  const changeable = remindersChangeable(a);
  const remind = (on: boolean) =>
    update.mutate(
      { id: a.id, send_reminders: on },
      {
        onSuccess: () => toast.success(on ? "Reminders on" : "Reminders off", a.title),
        onError: (e) => toast.error("Could not change the reminders", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  return (
    <div className="space-y-1.5">
      <p>
        <span className="text-text-muted">Confirmation: </span>
        {a.confirmation ? confirmationText(a.confirmation) : "Not sent with the booking"}
      </p>
      <div className="flex items-center gap-2">
        <Switch
          checked={a.send_reminders}
          disabled={!changeable || update.isPending}
          onCheckedChange={remind}
          aria-labelledby={`appt-${a.id}-reminders`}
        />
        <span id={`appt-${a.id}-reminders`}>Reminder texts before it</span>
      </div>
      {!changeable && !isClosed(a.status) && (
        <p className="text-xs text-text-muted">It has started: its reminders no longer change.</p>
      )}
    </div>
  );
}

/** Edit's and Reschedule's header: a way back to the details over the title, nothing saved. */
function SubHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <DialogHeader className="space-y-1">
      <button
        type="button"
        className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-muted hover:text-text"
        onClick={onBack}
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        View details
      </button>
      <DialogTitle>{title}</DialogTitle>
    </DialogHeader>
  );
}

function EditForm({ appointment: a, onDone }: { appointment: AppointmentDetail; onDone: () => void }) {
  const toast = useToast();
  const update = useUpdateAppointment();
  const [title, setTitle] = React.useState(a.title);
  const [notes, setNotes] = React.useState(a.notes ?? "");
  const [kind, setKind] = React.useState<MeetingLocationKind>(a.location_kind);
  const [detail, setDetail] = React.useState(a.location_detail ?? "");
  const [error, setError] = React.useState<string | null>(null);
  const needsDetail = (kind === "address" || kind === "custom") && !detail.trim();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || needsDetail) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: a.id,
        title: title.trim(),
        notes: notes.trim() || null,
        location_kind: kind,
        location_detail: detail.trim() || null,
      });
      toast.success("Appointment saved", title.trim());
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save. Please try again.");
    }
  };

  return (
    <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
      <SubHeader title="Edit appointment" onBack={onDone} />
      <DialogBody className="space-y-4">
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Title" htmlFor="appt-edit-title" required error={title.trim() ? undefined : "A title is needed"}>
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Meeting location" htmlFor="appt-edit-location">
          <Select value={kind} onValueChange={(k) => setKind(k as MeetingLocationKind)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LOCATION_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {LOCATION_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Location details" htmlFor="appt-edit-detail" error={needsDetail ? "Add the details for this location" : undefined}>
          <Input value={detail} maxLength={1000} placeholder={LOCATION_PLACEHOLDERS[kind]} onChange={(e) => setDetail(e.target.value)} />
        </Field>
        <Field label="Appointment description" htmlFor="appt-edit-notes">
          <Textarea rows={5} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <p className="text-xs text-text-muted">The time changes by rescheduling; the status at the foot of the details.</p>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Back to details
        </Button>
        <Button type="submit" loading={update.isPending} disabled={!title.trim() || needsDetail}>
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}

function Reschedule({ appointment: a, onDone }: { appointment: AppointmentDetail; onDone: (newId?: number) => void }) {
  const toast = useToast();
  const move = useRescheduleAppointment();
  const calendar = useCalendar(a.calendar_id);
  // A round-robin calendar set to reassign (ACL-290) offers everyone's times; otherwise the owner's.
  const reassign = calendar.data?.calendar_type === "round_robin" && calendar.data.reschedule_owner === "reassign";
  // Opens on the day it is booked for now, on the calendar's clock; a custom time starts from the
  // meeting's own (ACL-521).
  const [when, setWhen] = React.useState<When>(() =>
    openWhen(a.calendar_timezone, { day: localDay(a.starts_at, a.calendar_timezone), from: a.starts_at, to: a.ends_at }),
  );
  const [error, setError] = React.useState<string | null>(null);
  const time = chosenTime(when, Date.now());
  const duration = calendar.data?.duration_minutes ?? 0;
  // core-api refuses the time it already has; a slot is the calendar's duration long.
  const unchanged =
    time !== null &&
    Date.parse(time.start) === Date.parse(a.starts_at) &&
    (time.end ? Date.parse(time.end) : Date.parse(time.start) + duration * 60_000) === Date.parse(a.ends_at);

  const submit = async () => {
    if (!time) return;
    setError(null);
    try {
      const made = await move.mutateAsync({ id: a.id, ...time });
      toast.success(
        "Appointment rescheduled",
        timeRange(made.starts_at, made.ends_at, when.zone, calendar.data?.time_format, calendar.data?.locale),
      );
      onDone(made.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reschedule. Please try again.");
      setWhen((w) => withSlot(w, null));
    }
  };

  return (
    <>
      <SubHeader title="Reschedule" onBack={() => onDone()} />
      <DialogBody className="space-y-4">
        <p className="text-sm text-text-muted">
          Now: {timeRange(a.starts_at, a.ends_at, a.calendar_timezone, calendar.data?.time_format, calendar.data?.locale)}, with{" "}
          {a.owner?.name ?? "its owner"}. Pick the new time; this one is then closed as rescheduled and its history kept.
        </p>
        {error && <Callout tone="danger">{error}</Callout>}
        {calendar.data ? (
          <DayAndTime
            calendar={calendar.data}
            when={when}
            onWhen={setWhen}
            ignore={a.id}
            userId={reassign ? null : (a.owner?.id ?? null)}
            contactZone={a.contact?.timezone}
          />
        ) : calendar.isPending ? (
          <div className="grid place-items-center py-8">
            <Spinner />
          </div>
        ) : (
          <Callout tone="danger">Could not load the calendar.</Callout>
        )}
        <p className="text-xs text-text-muted">
          {when.mode === "custom"
            ? reassign
              ? `Round robin chooses among the staff on ${a.calendar_name} free at the custom time.`
              : `${a.owner?.name ?? "The owner"} has to be free then; this meeting's own time counts as free.`
            : reassign
              ? `The free times are anyone's on ${a.calendar_name}: round robin chooses who takes the new time.`
              : `The free times are ${a.owner?.name ?? "the owner"}'s on ${a.calendar_name}, counting this meeting's own time as free.`}
        </p>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={() => onDone()}>
          Back to details
        </Button>
        <Button onClick={() => void submit()} loading={move.isPending} disabled={!time || unchanged}>
          Move to this time
        </Button>
      </DialogFooter>
    </>
  );
}
